import { Dialog, Transition } from "@headlessui/react"
import React, { Fragment } from "react"

import { ModalProvider, useModal } from "@lib/context/modal-context"
import ArrowBack from "@modules/common/icons/arrow-back"
import X from "@modules/common/icons/x"

type SheetProps = {
  isOpen: boolean
  close: () => void
  children: React.ReactNode
}

const SideSheet = ({ isOpen, close, children }: SheetProps) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-modal" onClose={close}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </Transition.Child>

        <div className="fixed inset-0">
          <div className="absolute inset-0">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl">
                  <div className="relative flex h-full flex-col bg-white shadow-xl">
                    <button
                      onClick={() => {
                        window.history.replaceState({}, document.title, window.location.pathname)
                        close()
                      }}
                      className="absolute left-4 top-4 z-10 rounded-full bg-white p-2 transition-colors duration-200 hover:bg-gray-100"
                    >
                      <ArrowBack size={30} />
                    </button>
                    <ModalProvider close={close}>{children}</ModalProvider>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

const Title: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { close } = useModal()

  return (
    <Dialog.Title className="flex items-center justify-between p-4 sm:p-6">
      <div className="text-large-semi">{children}</div>
      <button onClick={close} className="text-gray-400 hover:text-gray-500">
        <X size={20} />
      </button>
    </Dialog.Title>
  )
}

const Description: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <Dialog.Description className="text-small-regular p-4 text-ui-fg-base sm:p-6">{children}</Dialog.Description>
}

const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className="flex-1 overflow-hidden p-4 sm:p-6">{children}</div>
}

const Footer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className="flex items-center justify-end gap-x-4 p-4 sm:p-6">{children}</div>
}

SideSheet.Title = Title
SideSheet.Description = Description
SideSheet.Body = Body
SideSheet.Footer = Footer

export default SideSheet
